<template>
  <div class="entry-container">
    <!-- Header Section - Same style as FormComp -->
    <div class="brief-section">
      <div class="brief-content">
        <h2>Thank you for signing up!</h2>
        <p>Your campaign creator request has been successfully submitted. We will review your information and get back to you shortly via email.</p>
      </div>
    </div>

    <!-- Success Message -->
    <div class="success-section">
      <div class="success-icon">✓</div>
      <h3>We'll be in touch soon</h3>
      <p>Our team will review your campaign configuration and contact you within 24-48 hours to get your experience up and running.</p>

      <!-- Configure Another Campaign Button -->
      <div class="action-section">
        <button @click="configureAnotherCampaign" class="configure-button">
          Configure Another Campaign
        </button>
        <p class="action-description">Create a new campaign with your existing details</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth.store'

export default defineComponent({
  name: 'EntryView',
  setup() {
    const router = useRouter()
    const authStore = useAuthStore()

    const configureAnotherCampaign = () => {
      // Navigate to the form with a query parameter to indicate prefill
      router.push({ path: '/', query: { prefill: 'true' } })
    }

    return {
      configureAnotherCampaign,
    }
  },
})
</script>

<style scoped>
.entry-container {
  display: flex;
  flex-direction: column;
  width: 80%;
  max-width: 37.5rem; /* 600px */
  padding: 1.5625rem; /* 25px */
  margin: 6.25rem auto 3.125rem; /* 100px auto 50px */
  background-color: var(--color-background-soft);
  border-radius: 1rem; /* 16px */
  box-shadow:
    0 0.625rem 1.875rem rgba(0, 0, 0, 0.3),
    0 0 0 0.0625rem rgba(var(--vt-c-accent-rgb), 0.1); /* 10px 30px, 1px */
  backdrop-filter: blur(0.625rem); /* 10px */
}

/* Brief Section Styling - Same as FormComp */
.brief-section {
  margin-bottom: 1.875rem; /* 30px */
  padding: 1.5625rem; /* 25px */
  background: linear-gradient(135deg, rgba(var(--vt-c-accent-rgb), 0.1) 0%, var(--color-background-tertiary) 100%);
  border-radius: 1rem; /* 16px */
  box-shadow: 0 0.375rem 1.25rem rgba(0, 0, 0, 0.1); /* 6px 20px */
  position: relative;
  overflow: hidden;
  border-left: 0.25rem solid var(--vt-c-accent); /* 4px */
}

.brief-content h2 {
  margin: 0 0 0.9375rem 0; /* 15px */
  color: var(--color-heading);
  font-size: 2.4rem;
  font-weight: 700;
  text-align: center;
  font-family: var(--font-sans);
  text-shadow: 0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.1); /* 1px 3px */
}

.brief-content p {
  margin: 0;
  color: var(--color-text);
  font-size: 1.05rem;
  line-height: 1.6;
  text-align: center;
  font-family: var(--font-sans);
}

/* Success Section */
.success-section {
  text-align: center;
  padding: 1.875rem; /* 30px */
  background-color: var(--color-background-tertiary);
  border-radius: 0.75rem; /* 12px */
  box-shadow: 0 0.25rem 0.9375rem rgba(0, 0, 0, 0.1); /* 4px 15px */
  border-left: 0.25rem solid var(--vt-c-accent); /* 4px */
}

.success-icon {
  width: 3.75rem; /* 60px */
  height: 3.75rem; /* 60px */
  background-color: var(--vt-c-accent);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: var(--color-background);
  font-weight: bold;
  margin: 0 auto 1.25rem; /* 20px */
  box-shadow: 0 0.25rem 0.9375rem rgba(var(--vt-c-accent-rgb), 0.3); /* 4px 15px */
}

.success-section h3 {
  margin: 0 0 0.9375rem 0; /* 15px */
  color: var(--color-heading);
  font-size: 1.4rem;
  font-weight: 600;
  font-family: var(--font-sans);
}

.success-section p {
  margin: 0;
  color: var(--color-text);
  font-size: 1rem;
  line-height: 1.6;
  font-family: var(--font-sans);
}

/* Action Section */
.action-section {
  margin-top: 1.875rem; /* 30px */
  padding-top: 1.5625rem; /* 25px */
  border-top: 0.0625rem solid rgba(var(--vt-c-accent-rgb), 0.2); /* 1px */
}

.configure-button {
  background: linear-gradient(135deg, var(--vt-c-accent) 0%, #7ae7b9 100%);
  color: var(--color-background);
  border: none;
  padding: 0.9375rem 1.875rem; /* 15px 30px */
  border-radius: 0.75rem; /* 12px */
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: var(--font-sans);
  box-shadow: 0 0.25rem 0.9375rem rgba(var(--vt-c-accent-rgb), 0.3); /* 4px 15px */
  margin-bottom: 0.625rem; /* 10px */
}

.configure-button:hover {
  transform: translateY(-0.125rem); /* -2px */
  box-shadow: 0 0.375rem 1.25rem rgba(var(--vt-c-accent-rgb), 0.4); /* 6px 20px */
  background: linear-gradient(135deg, #7ae7b9 0%, var(--vt-c-accent) 100%);
}

.configure-button:active {
  transform: translateY(0);
  box-shadow: 0 0.125rem 0.625rem rgba(var(--vt-c-accent-rgb), 0.3); /* 2px 10px */
}

.action-description {
  color: var(--vt-c-text-muted);
  font-size: 0.9rem;
  margin-top: 0.5rem !important; /* 8px */
  font-style: italic;
}

/* Responsive Design */
@media (max-width: 768px) {
  .entry-container {
    width: 90%;
    margin-top: 3.125rem; /* 50px */
    padding: 1.25rem; /* 20px */
  }

  .brief-section {
    margin-bottom: 1.5625rem; /* 25px */
    padding: 1.25rem; /* 20px */
  }

  .brief-content h2 {
    font-size: 2rem;
  }

  .success-section {
    padding: 1.5625rem; /* 25px */
  }

  .success-icon {
    width: 3.125rem; /* 50px */
    height: 3.125rem; /* 50px */
    font-size: 1.5rem;
  }

  .success-section h3 {
    font-size: 1.2rem;
  }

  .configure-button {
    padding: 0.75rem 1.5625rem; /* 12px 25px */
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .entry-container {
    padding: 0.9375rem; /* 15px */
  }

  .brief-section {
    padding: 0.9375rem; /* 15px */
  }

  .brief-content h2 {
    font-size: 1.7rem;
  }

  .brief-content p {
    font-size: 1rem;
  }

  .success-section {
    padding: 1.25rem; /* 20px */
  }

  .success-section h3 {
    font-size: 1.1rem;
  }

  .success-section p {
    font-size: 0.95rem;
  }

  .configure-button {
    padding: 0.625rem 1.25rem; /* 10px 20px */
    font-size: 0.95rem;
  }

  .action-description {
    font-size: 0.85rem;
  }
}
</style>
